import { useSelector, useDispatch } from 'react-redux';
import { insertTrackEvent, loginFailure } from '@comeon/analytics';
import { useCloseCurrentContext } from '~/hooks/use-close-context.js';
import useBasePathname from '~/hooks/use-base-pathname';
import { showSupportDialogBox } from '~/components/support/ducks/index.js';
import {
	showNotification,
	hideBinaryNotification,
	showBinaryQuestion,
	selectBinaryQuestionData
} from '~/components/notifications/ducks/notifications.js';
import { loginFailed } from '../../ducks/index.js';
import { reopenAccount } from '../../index.js';
import BinaryQuestion from '~/components/binary-question';
import { requestReopenTrustly, reLoginCancelledTrustly } from '~/components/guest-login/guest-login-helper';
import useFullLogin from '~/components/authentication/login/use-full-login.js';
import { ROUTE_RESET_PASSWORD, ROUTE_REGISTER, ROUTE_LOGIN } from '~/components/router/constants.js';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer.js';
import { FORM_NOTIFICATION_ERROR_CODES } from '../form-notification.js';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import useTranslations from '~/components/translation/use-translations.ts';
import { MODAL_POPUP_TEXT_KEYS } from '../login-text-keys';
import { LOGIN_TYPES } from '~/components/authentication/ducks/constants';
import { showQueuedNotification } from '~/components/notifications/ducks/actions';
import { selectIntRef } from '~/components/account/ducks/index.js';

const RESET_PASSWORD_ROUTES_REGEXP =
	/reset-password-start|reset-password-email|reset-password-phone|reset-password-code|reset-password-welcome|reset-password-dob-verification|new-password/g;

const invalidErrorCodes = ['RG_DUPLICATE_PLAYER', ...FORM_NOTIFICATION_ERROR_CODES];

let reactivatePromptEventAdded = false;
const dataLayerReactivateAccountPromptEvent = () => {
	insertTrackEvent({
		category: 'Sidebar',
		action: 'Account reactivation',
		label: 'Prompt visible'
	});
	reactivatePromptEventAdded = true;
};

const dataLayerReactivatedAccountEvent = () => {
	insertTrackEvent({
		category: 'Sidebar',
		action: 'Account reactivation',
		label: 'Reactivated'
	});
};

const dataLayerRejectReactivateEvent = () => {
	insertTrackEvent({
		category: 'Sidebar',
		action: 'Account reactivation',
		label: 'Rejected'
	});
};

async function requestReopen(payload, withinSidebar, dispatch, onReopening) {
	const resp = await reopenAccount(payload);

	if (resp.status === 'SUCCESS') {
		if (resp.result.resetPW) {
			dispatch(
				showNotification({
					message: resp.result.msg,
					color: 'green',
					autoHide: true,
					openInSidebar: withinSidebar
				})
			);

			onReopening?.();
		} else {
			dispatch(loginFailed(resp.result));
		}
		dataLayerReactivatedAccountEvent();
	}
}

const ModalPopupLogin = () => {
	/**
	 * Getting popup payload
	 */
	const { popupMessageData } = useSelector(state => state.notifications) ?? {};
	const intRef = useSelector(selectIntRef);
	const binaryQuestionData = useSelector(selectBinaryQuestionData) ?? {};
	const { translations, isLoadingTranslations } = useTranslations(
		popupMessageData.errorCode ? MODAL_POPUP_TEXT_KEYS : []
	);

	const dispatch = useDispatch();
	const closeCurrentContext = useCloseCurrentContext();
	const { redirectToLogin, handleLogin } = useFullLogin();
	const router = useRouter();
	const transformPath = useContextualPathTransformer();
	const location = useLocation();
	const basePathname = useBasePathname();

	const { errorCode, errorDescription, withinSidebar = false, trustlyPopup = false, result = {} } = popupMessageData;

	const hideNotification = () => {
		if (trustlyPopup) {
			if (location.state && location.state.from) {
				closeCurrentContext({ toLocation: location.state.from });
			} else {
				closeCurrentContext();
			}
		}

		// Redirect user to login page in error accrue in reset-password flow
		if (RESET_PASSWORD_ROUTES_REGEXP.test(window.location.href)) {
			router.push(transformPath(ROUTE_LOGIN));
		}
		dispatch(hideBinaryNotification());
	};

	useEffect(() => {
		if (errorCode) {
			switch (errorCode) {
				case 'ROFUS_EXCLUDED_TEMPORARILY_ON_LOGIN':
					dispatch(
						showBinaryQuestion({
							messageKey: 'MOBILE_ROFUS_EXCLUDED_TEMPORARILY_ON_ROFUS_TEXT',
							headerKey: 'ACCOUNT_CLOSED_HEADER',
							yesTextKey: 'OPEN_SUPPORT_BUTTON',
							onYesClick: () => {
								dispatch(showSupportDialogBox(true));
								hideNotification();
							},
							noTextKey: 'CLOSE_BUTTON',
							onNoClick: hideNotification
						})
					);
					break;
				case 'BANKID_ACC_LOCKED_REOPEN':
					dispatch(
						showBinaryQuestion({
							messageKey: 'ACCOUNT_CLOSED_REOPEN_NOW_MESSAGE',
							headerKey: 'ACCOUNT_CLOSED_HEADER',
							yesTextKey: 'BUTTON_ACCOUNT_REOPEN_NOW',
							onYesClick: () => {
								requestReopen(
									{
										email: result.email,
										code: result.token,
										playerIdString: result.playerId
									},
									withinSidebar,
									dispatch,
									result.onReopening
								);
								hideNotification();
							},
							noTextKey: 'BUTTON_CANCEL',
							onNoClick: () => {
								hideNotification();
								dataLayerRejectReactivateEvent();
							}
						})
					);
					if (!reactivatePromptEventAdded) {
						dataLayerReactivateAccountPromptEvent();
					}
					break;
				case 'RG_REGULAR_ACCOUNT_CLOSED_REOPEN':
				case 'MOBILE_PLAYER_SHOULD_REOPEN':
					dispatch(
						showBinaryQuestion({
							messageKey: 'ACCOUNT_CLOSED_REOPEN_NOW_MESSAGE',
							headerKey: 'ACCOUNT_CLOSED_HEADER',
							yesTextKey: 'BUTTON_ACCOUNT_REOPEN_NOW',
							onYesClick: () => {
								requestReopen(
									{
										email: result.email,
										code: result.token,
										playerIdString: result.playerId
									},
									withinSidebar,
									dispatch,
									() => handleLogin({ password: result.credentials, type: LOGIN_TYPES.REOPEN })
								);
								hideNotification();
							},
							noTextKey: 'BUTTON_CANCEL',
							onNoClick: () => {
								hideNotification();
								dataLayerRejectReactivateEvent();
							}
						})
					);
					if (!reactivatePromptEventAdded) {
						dataLayerReactivateAccountPromptEvent();
					}
					break;
				case 'TRUSTLY_ACCOUNT_LOCKED_REOPEN':
					dispatch(
						showBinaryQuestion({
							messageKey: 'ACCOUNT_CLOSED_REOPEN_NOW_MESSAGE',
							headerKey: 'ACCOUNT_CLOSED_HEADER',
							yesTextKey: 'BUTTON_ACCOUNT_REOPEN_NOW',
							onYesClick: () => {
								requestReopenTrustly(
									result.intRef,
									result.onReopening,
									result.onFailedReopen,
									dataLayerReactivatedAccountEvent
								);
								dispatch(hideBinaryNotification());
							},
							noTextKey: 'BUTTON_CANCEL',
							onNoClick: () => {
								reLoginCancelledTrustly(intRef);
								hideNotification();
								dataLayerRejectReactivateEvent();
							}
						})
					);
					if (!reactivatePromptEventAdded) {
						dataLayerReactivateAccountPromptEvent();
					}
					break;
				case 'MOBILE_PLAYER_SHOULD_REOPEN_RELOGIN':
					dispatch(
						showBinaryQuestion({
							messageKey: 'ACCOUNT_CLOSED_REOPEN_NOW_MESSAGE',
							headerKey: 'ACCOUNT_CLOSED_HEADER',
							yesTextKey: 'BUTTON_ACCOUNT_REOPEN_NOW',
							onYesClick: () => {
								requestReopen(
									{
										email: result.email,
										code: result.token,
										playerIdString: result.playerId
									},
									withinSidebar,
									dispatch,
									redirectToLogin
								);
								hideNotification();
							},
							noTextKey: 'BUTTON_CANCEL',
							onNoClick: () => {
								hideNotification();
								dataLayerRejectReactivateEvent();
							}
						})
					);
					if (!reactivatePromptEventAdded) {
						dataLayerReactivateAccountPromptEvent();
					}
					break;
				case 'TRUSTLY_ACCOUNT_ERROR':
					dispatch(
						showBinaryQuestion({
							headerKey: `${errorDescription}_TITLE`,
							messageKey: `${errorDescription}_TEXT`,
							noTextKey: 'MOBILE_TRUSTLY_LOGIN_RESULT',
							onNoClick: hideNotification
						})
					);
					break;
				case 'PlayerException_BLACKLISTED':
				case 'MOBILE_PLAYER_CLOSED_ACCOUNT':
				case 'BANKID_ACC_LOCKED':
				case 'ACC_LOCKED':
					dispatch(
						showBinaryQuestion({
							messageKey: 'ACCOUNT_CLOSED_CONTACT_SUPPORT_MESSAGE',
							headerKey: 'ACCOUNT_CLOSED_HEADER',
							yesTextKey: 'BUTTON_CUSTOMER_SUPPORT',
							onYesClick: () => {
								dispatch(showSupportDialogBox(true));
								router.push(basePathname);
								hideNotification();
							},
							noTextKey: 'BUTTON_CANCEL',
							onNoClick: () => {
								router.push(basePathname);
								hideNotification();
							}
						})
					);
					break;
				case 'MOBILE_PLAYER_SUSPEND_TIME_ACCOUNT':
				case 'MOBILE_PLAYER_SUSPEND_TIME_ACCOUNT_OVER':
					dispatch(
						showBinaryQuestion({
							message: errorDescription,
							headerKey: 'ACCOUNT_CLOSED_HEADER',
							yesTextKey: 'BUTTON_CUSTOMER_SUPPORT',
							onYesClick: () => {
								dispatch(showSupportDialogBox(true));
								hideNotification();
							},
							noTextKey: 'BUTTON_CANCEL',
							onNoClick: hideNotification
						})
					);
					break;
				case 'PLAYER_EXCEPTION_COOL_OFF_PERIOD_ONGOING':
					dispatch(
						showBinaryQuestion({
							message: errorDescription,
							headerKey: 'COOLOFF_PERIOD',
							yesTextKey: 'BUTTON_CUSTOMER_SUPPORT',
							onYesClick: () => {
								dispatch(showSupportDialogBox(true));
								hideNotification();
							},
							noTextKey: 'BUTTON_CANCEL',
							onNoClick: hideNotification
						})
					);
					break;
				case 'LOGIN_FAILURE_SCHUFA_UNVERIFIED':
				case 'SCHUFA_BIRTHDATE_MISMATCH':
					dispatch(
						showBinaryQuestion({
							messageKey: 'ACCOUNT_SCHUFA_UNVERIFIED_SUPPORT_MESSAGE',
							headerKey: 'ACCOUNT_SCHUFA_UNVERIFIED_HEADER',
							noTextKey: 'BUTTON_CUSTOMER_SUPPORT',
							onNoClick: () => {
								dispatch(showSupportDialogBox(true));
								hideNotification();
							},
							yesTextKey: 'CLOSE_BUTTON',
							onYesClick: hideNotification
						})
					);
					break;
				case 'PLAYER_EXCEPTION_PASSWORD_REMINDER':
					dispatch(
						showBinaryQuestion({
							messageKey: 'PASSWORD_REMINDER',
							headerKey: 'PASSWORD_REMINDER_ERROR_HEADER',
							yesTextKey: 'BUTTON_FORGOT_PASSWORD',
							onYesClick: () => {
								router.push(transformPath(ROUTE_RESET_PASSWORD));
							},
							noTextKey: 'BUTTON_CANCEL',
							onNoClick: hideNotification
						})
					);
					break;
				case 'ACCOUNT_INFORMATION_NO_RESET_PW':
					dispatch(
						showBinaryQuestion({
							headerKey: 'ACCOUNT_INFORMATION',
							message: result.msg,
							noTextKey: 'BUTTON_OK',
							onNoClick: hideNotification
						})
					);
					break;
				case 'REGISTRATION_SMS_VERIFICATION_RETRY_COUNT_EXCEEDED':
					dispatch(
						showBinaryQuestion({
							shouldOpenBinary: false
						})
					);

					dispatch(
						showNotification({
							messageKey: 'LOGIN_TWO_FACTOR_SMS_ERROR',
							color: 'red',
							autoHide: true,
							openInSidebar: withinSidebar
						})
					);
					break;
				case 'LUGAS_GENERAL_ERROR':
				case 'LUGAS_FIELD_VALIDATION_FAILED':
					loginFailure({
						method: 'UsernamePw',
						error: `${errorCode} - ${translations[`${errorDescription}_MESSAGE`]}`
					});

					dispatch(
						showBinaryQuestion({
							headerKey: `${errorCode}_HEADER`,
							messageKey: `${errorCode}_MESSAGE`,
							yesTextKey: 'LOG_OUT',
							onYesClick: hideNotification
						})
					);
					break;
				case 'EMAIL_REQUIRED':
					dispatch(
						showBinaryQuestion({
							headerKey: `${errorCode}_HEADER`,
							messageKey: `${errorCode}_MESSAGE`,
							yesTextKey: 'TRYAGAIN',
							onYesClick: () => {
								hideNotification();
							},
							noTextKey: 'BUTTON_CANCEL',
							onNoClick: hideNotification
						})
					);
					break;
				case 'PLAYER_NOT_REGISTERED':
					dispatch(
						showBinaryQuestion({
							headerKey: `${errorCode}_HEADER`,
							messageKey: `${errorCode}_MESSAGE`,
							yesTextKey: 'CREATE_ACCOUNT',
							noTextKey: 'BUTTON_CANCEL',
							onNoClick: hideNotification,
							onYesClick: () => {
								router.push(transformPath(ROUTE_REGISTER));
								hideNotification();
							},
							reverseOrder: true
						})
					);
					break;
				case 'LOGIN_PHONE_PLAYER_NOT_FOUND':
					dispatch(
						showBinaryQuestion({
							headerKey: `${errorCode}_HEADER`,
							messageKey: `${errorCode}_MESSAGE`,
							yesTextKey: 'CREATE_ACCOUNT',
							noTextKey: 'BUTTON_CANCEL',
							onNoClick: hideNotification,
							onYesClick: () => {
								router.push(transformPath(ROUTE_REGISTER));
								hideNotification();
							},
							reverseOrder: true
						})
					);
					break;
				case 'ACCOUNT_IS_LOCKED_TOO_MANY_LOGIN_ATTEMPTS':
					dispatch(
						showBinaryQuestion({
							headerKey: `${errorCode}_HEADER`,
							message: errorDescription,
							noTextKey: 'FORGOT_PASSWORD',
							yesTextKey: 'BUTTON_CANCEL',
							onYesClick: hideNotification,
							onNoClick: () => {
								router.push(transformPath(ROUTE_RESET_PASSWORD));
							}
						})
					);
					break;
				case 'LOGIN_LINKED_GUEST_ACCOUNT_PLAYING':
					dispatch(
						showNotification({
							messageKey: 'LOGIN_LINKED_TO_GUEST_ACCOUNT_PLAYING',
							color: 'red',
							autoHide: true,
							autoHideDuration: 8000,
							openInSidebar: withinSidebar
						})
					);
					router.push(transformPath(ROUTE_LOGIN));
					break;
				case 'PLAYER_EMAIL_NOT_FOUND':
					dispatch(
						showBinaryQuestion({
							headerKey: `${errorCode}_HEADER`,
							message: errorDescription,
							noTextKey: 'CREATE_ACCOUNT',
							yesTextKey: 'BUTTON_CANCEL',
							onYesClick: hideNotification,
							onNoClick: () => {
								router.push(transformPath(ROUTE_REGISTER));
								hideNotification();
							}
						})
					);
					break;
				case 'PLAYER_EXCEPTION_CLOSED_ACCOUNT':
					dispatch(
						showNotification({
							messageKey: 'PLAYER_EXCEPTION_CLOSED_ACCOUNT_MSG',
							color: 'red',
							autoHide: true,
							openInSidebar: withinSidebar
						})
					);

					router.push(transformPath(ROUTE_LOGIN));

					break;
				case 'CRUKS_EXCLUDED_ON_REGISTRATION_LOGIN':
				case 'CRUKS_CODE_INVALID':
				case 'CRUKS_GENERAL_EXCEPTION':
				case 'CRUKS_UNAUTHORIZED_RESOURCE':
					dispatch(
						showQueuedNotification({
							message: errorDescription,
							color: 'red',
							openInSidebar: withinSidebar
						})
					);

					break;
				default:
					if (FORM_NOTIFICATION_ERROR_CODES.includes(errorCode)) {
						break;
					}

					dispatch(
						showBinaryQuestion({
							shouldOpenBinary: false
						})
					);

					dispatch(
						showNotification({
							message: errorDescription || translations[errorCode] || '',
							color: 'red',
							autoHide: true,
							openInSidebar: withinSidebar
						})
					);
					break;
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [errorCode]);

	if ((!errorCode && !errorDescription) || invalidErrorCodes.includes(errorCode) || isLoadingTranslations) {
		return null;
	}

	const {
		shouldOpenBinary,
		headerKey,
		header,
		messageKey,
		message,
		yesTextKey,
		yesText,
		onYesClick,
		noTextKey,
		noText,
		onNoClick,
		thirdKeyClick,
		thirdKey,
		thirdText,
		reverseOrder
	} = binaryQuestionData;

	return (
		<BinaryQuestion
			renderInParent
			shouldOpenBinary={shouldOpenBinary}
			title={headerKey ? translations[headerKey] : header}
			text={messageKey ? translations[messageKey] : message}
			yesText={yesTextKey ? translations[yesTextKey] : yesText}
			onYesClick={onYesClick}
			noText={noTextKey ? translations[noTextKey] : noText}
			onNoClick={onNoClick}
			thirdKeyClick={thirdKeyClick}
			thirdText={thirdKey ? translations[thirdKey] : thirdText}
			reverseOrder={reverseOrder || false}
		/>
	);
};

export default ModalPopupLogin;
